import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartPanel from "./ChartPanel";
import KpiCard from "./KpiCard";
import ClienteDialog from "../../Vendedor/components/ClienteDialog";
import { apiFetch } from "../../api";
import { colors } from "../utils/datosTheme";
import { formatNumber } from "../utils/datosFormatters";

function ChartTitle({ title, description }) {
  return (
    <Box>
      <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15 }}>
        {title}
      </Typography>
      <Typography sx={{ color: colors.muted, fontWeight: 650, fontSize: 11.5, mt: 0.35, lineHeight: 1.25 }}>
        {description}
      </Typography>
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const tableCellSx = {
  color: colors.ink,
  fontSize: 12,
  borderColor: colors.line,
  whiteSpace: "nowrap",
};

export default function EmbudoDashboard({ data }) {
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openClient, setOpenClient] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientError, setClientError] = useState("");
  const kpis = data?.kpis || {};
  const leadsPorEstadio = data?.leads_por_estadio || [];
  const bajasPorEstadio = data?.bajas_por_estadio || [];
  const cierresPorEstadio = data?.cierres_por_estadio || [];
  const estancadosPorEstadio = data?.estancados_por_estadio || [];
  const leadsEstancados = useMemo(() => data?.leads_estancados || [], [data]);

  const hasLeads = leadsPorEstadio.some((item) => Number(item.total || 0) > 0);
  const hasBajas = bajasPorEstadio.some((item) => Number(item.bajas || 0) > 0);
  const hasCierres = cierresPorEstadio.some((item) => Number(item.total || 0) > 0);
  const hasEstancados = estancadosPorEstadio.some((item) => Number(item.total || 0) > 0);
  const hasTable = leadsEstancados.length > 0;
  const paginatedLeads = useMemo(
    () => leadsEstancados.slice(tablePage * rowsPerPage, tablePage * rowsPerPage + rowsPerPage),
    [leadsEstancados, tablePage, rowsPerPage]
  );

  useEffect(() => {
    setTablePage(0);
  }, [leadsEstancados.length, rowsPerPage]);

  const handleOpenCliente = async (clienteId) => {
    if (!clienteId) return;
    setOpenClient(true);
    setClientData(null);
    setClientLoading(true);
    setClientError("");
    try {
      const res = await apiFetch(`/api/clientes/${clienteId}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const nextClient = await res.json();
      setClientData(nextClient);
    } catch (e) {
      setOpenClient(false);
      setClientError("No se pudo abrir el cliente.");
    } finally {
      setClientLoading(false);
    }
  };

  const handleChangeClient = (nextClient) => {
    setClientData(nextClient);
  };

  const handleSaveClient = async (cliente) => {
    if (!cliente?.id) return null;
    setClientSaving(true);
    try {
      const payload = {
        nombre: cliente.nombre,
        telefono: cliente.telefono || null,
        email: cliente.email || null,
        obtenido_de: cliente.obtenido_de || null,
        observacion: cliente.observacion || null,
      };
      const res = await apiFetch(`/api/clientes/${cliente.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const updated = await res.json();
      setClientData(updated);
      return updated;
    } finally {
      setClientSaving(false);
    }
  };

  const kpiDescriptions = {
    estadio_mas_problematico: "Estadio con más bajas en el período",
    bajas_estadio_problematico: "Bajas del estadio problemático",
    estancados_estadio_problematico: "Leads activos estancados en ese estadio",
    leads_estancados: "Activos sin movimiento hace más de 7 días",
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {[
          "estadio_mas_problematico",
          "bajas_estadio_problematico",
          "estancados_estadio_problematico",
          "leads_estancados",
        ].map((id) => (
          <KpiCard
            key={id}
            id={id}
            value={id === "estadio_mas_problematico" ? (kpis[id] || "Sin datos") : kpis[id]}
            helper={
              id === "estadio_mas_problematico" && kpis.estadio_mas_problematico
                ? `${formatNumber(kpis.bajas_estadio_problematico)} bajas · ${formatNumber(kpis.estancados_estadio_problematico)} estancados`
                : undefined
            }
            description={kpiDescriptions[id]}
          />
        ))}
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel
          title={
            <ChartTitle
              title="Leads por estadio"
              description="Foto actual de las consultas agrupadas por su estadio comercial."
            />
          }
          empty={!hasLeads}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={leadsPorEstadio} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="estadio" width={116} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Bar dataKey="total" name="Leads" fill={colors.blue} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 1.5,
          mt: 1.5,
        }}
      >
        <ChartPanel
          title={
            <ChartTitle
              title="Bajas por estadio"
              description="Bajas del período agrupadas por el estadio comercial actual del lead."
            />
          }
          empty={!hasBajas}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bajasPorEstadio} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="estadio" width={116} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Bar dataKey="bajas" name="Bajas" fill={colors.coral} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title={
            <ChartTitle
              title="Leads estancados por estadio"
              description="Leads activos sin devoluciones ni movimientos registrados durante más de 7 días."
            />
          }
          empty={!hasEstancados}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={estancadosPorEstadio} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="estadio" width={116} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Bar dataKey="total" name="Estancados" fill={colors.amber} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <ChartPanel
          title={
            <ChartTitle
              title="Cierres por estadio"
              description="Compara cómo terminan las oportunidades según el estadio comercial actual asociado a cada consulta."
            />
          }
          empty={!hasCierres}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cierresPorEstadio} layout="vertical" margin={{ top: 6, right: 18, left: 18, bottom: 0 }}>
              <CartesianGrid stroke={colors.softLine} horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} allowDecimals={false} />
              <YAxis type="category" dataKey="estadio" width={116} tickLine={false} axisLine={false} tick={{ fill: colors.muted, fontSize: 12 }} />
              <RechartsTooltip formatter={(value) => formatNumber(value)} />
              <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
              <Bar dataKey="ventas" stackId="cierres" name="Ventas" fill={colors.green} radius={[0, 0, 0, 0]} />
              <Bar dataKey="bajas" stackId="cierres" name="Bajas" fill={colors.coral} radius={[0, 0, 0, 0]} />
              <Bar dataKey="futuros" stackId="cierres" name="Futuros" fill={colors.amber} radius={[0, 5, 5, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        {clientError && <Alert severity="error" sx={{ mb: 1.5 }}>{clientError}</Alert>}
        <Box
          sx={{
            border: `1px solid ${colors.line}`,
            borderRadius: "8px",
            backgroundColor: colors.panel,
            boxShadow: "0 10px 24px rgba(15, 42, 89, 0.07)",
            p: { xs: 1.5, sm: 2 },
            minWidth: 0,
          }}
        >
          <Typography sx={{ color: colors.ink, fontWeight: 950, fontSize: 15, mb: 0.5 }}>
            Leads estancados
          </Typography>
          <Typography sx={{ color: colors.muted, fontWeight: 650, fontSize: 11.5, mb: 1.5 }}>
            Consultas activas ordenadas por más días sin movimiento.
          </Typography>
          {hasTable ? (
            <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Cliente", "Consulta", "Vendedor", "Vehículo", "Estadio", "Último movimiento", "Días"].map((head) => (
                      <TableCell
                        key={head}
                        align={head === "Días" ? "right" : "left"}
                        sx={{
                          color: colors.ink,
                          fontWeight: 950,
                          fontSize: 12,
                          bgcolor: "#f7f7f9",
                          borderColor: colors.line,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLeads.map((row) => (
                    <TableRow key={row.consulta_id}>
                      <TableCell sx={{ ...tableCellSx, fontWeight: 850 }}>
                        {row.cliente_id ? (
                          <ButtonBase
                            onClick={() => handleOpenCliente(row.cliente_id)}
                            sx={{
                              color: colors.ink,
                              font: "inherit",
                              fontWeight: 900,
                              textAlign: "left",
                              justifyContent: "flex-start",
                              borderRadius: "4px",
                              "&:hover": { color: colors.blue, textDecoration: "underline" },
                              "&:focus-visible": {
                                outline: `2px solid ${colors.blue}`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            {row.cliente_nombre || "-"}
                          </ButtonBase>
                        ) : (
                          row.cliente_nombre || "-"
                        )}
                      </TableCell>
                      <TableCell sx={tableCellSx}>{formatNumber(row.consulta_id)}</TableCell>
                      <TableCell sx={tableCellSx}>{row.vendedor_nombre || "Sin vendedor"}</TableCell>
                      <TableCell sx={tableCellSx}>{row.vehiculo_consultado || "-"}</TableCell>
                      <TableCell sx={tableCellSx}>{row.estadio}</TableCell>
                      <TableCell sx={tableCellSx}>{formatDateTime(row.ultimo_movimiento)}</TableCell>
                      <TableCell align="right" sx={tableCellSx}>{formatNumber(row.dias_sin_movimiento)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={leadsEstancados.length}
              page={tablePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(_, nextPage) => setTablePage(nextPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setTablePage(0);
              }}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                color: colors.ink,
                borderTop: `1px solid ${colors.line}`,
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: 12,
                  fontWeight: 800,
                },
              }}
            />
            </>
          ) : (
            <Box
              sx={{
                minHeight: 86,
                display: "grid",
                placeItems: "center",
                border: `1px dashed ${colors.line}`,
                borderRadius: "8px",
                color: colors.muted,
                bgcolor: "rgba(246,248,252,0.65)",
              }}
            >
              <Typography sx={{ fontWeight: 850, fontSize: 13 }}>
                Sin leads estancados
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <ClienteDialog
        open={openClient}
        onClose={() => setOpenClient(false)}
        cliente={clientData}
        onChange={handleChangeClient}
        onSave={handleSaveClient}
        onOpenSeguimiento={() => {}}
        saving={clientSaving}
        loading={clientLoading}
      />
    </>
  );
}
